import { Fragment, jsx } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useThrottle } from "@chronosphereio/shared";
import { PANEL_TOOLTIP_THROTTLE_MS } from "./model.js";
const usePanelTooltipStore = create()(devtools((set, get)=>({
        mousePos: void 0,
        pinnedPanelKey: void 0,
        pinnedCoords: void 0,
        activePanelKey: void 0,
        setMousePos: (coords)=>set({
                mousePos: coords
            }, false, '[PanelTooltip] setMousePos'),
        setActivePanelKey: (panelKey)=>set({
                activePanelKey: panelKey
            }, false, '[PanelTooltip] setActivePanelKey'),
        handlePin: (panelKey, coords)=>{
            set({
                pinnedPanelKey: panelKey,
                pinnedCoords: coords
            }, false, '[PanelTooltip] handlePin');
        },
        handleUnpin: ()=>set({
                pinnedPanelKey: void 0,
                pinnedCoords: void 0
            }, false, '[PanelTooltip] handleUnpin'),
        isPanelPinned: (panelKey)=>get().pinnedPanelKey === panelKey
    }), {
    name: 'PanelTooltipStore'
}));
function PanelTooltipProvider({ children }) {
    const setMousePos = usePanelTooltipStore((state)=>state.setMousePos);
    const handleUnpin = usePanelTooltipStore((state)=>state.handleUnpin);
    const pinnedPanelKey = usePanelTooltipStore((state)=>state.pinnedPanelKey);
    const setFromEvent = (e)=>{
        setMousePos({
            page: {
                x: e.pageX,
                y: e.pageY
            },
            client: {
                x: e.clientX,
                y: e.clientY
            },
            plotCanvas: {
                x: e.zrX ?? e.offsetX,
                y: e.zrY ?? e.offsetY
            }
        });
    };
    const throttledSetFromEvent = useThrottle(setFromEvent, PANEL_TOOLTIP_THROTTLE_MS);
    useEffect(()=>{
        const eventListener = (event)=>{
            throttledSetFromEvent(event);
        };
        window.addEventListener('mousemove', eventListener);
        return ()=>{
            window.removeEventListener('mousemove', eventListener);
        };
    }, [
        throttledSetFromEvent
    ]);
    const handleGlobalClickRef = useRef(()=>{});
    useEffect(()=>{
        handleGlobalClickRef.current = (_e)=>{
            if (pinnedPanelKey) handleUnpin();
        };
    }, [
        pinnedPanelKey,
        handleUnpin
    ]);
    useEffect(()=>{
        const globalClickHandler = (e)=>{
            handleGlobalClickRef.current(e);
        };
        window.addEventListener('click', globalClickHandler);
        return ()=>{
            window.removeEventListener('click', globalClickHandler);
        };
    }, []);
    return /*#__PURE__*/ jsx(Fragment, {
        children: children
    });
}
function usePanelTooltipActions() {
    const setActivePanelKey = usePanelTooltipStore((state)=>state.setActivePanelKey);
    const handlePin = usePanelTooltipStore((state)=>state.handlePin);
    const handleUnpin = usePanelTooltipStore((state)=>state.handleUnpin);
    const isPanelPinned = usePanelTooltipStore((state)=>state.isPanelPinned);
    return {
        setActivePanelKey,
        handlePin,
        handleUnpin,
        isPanelPinned
    };
}
function usePanelTooltipState() {
    const mousePos = usePanelTooltipStore((state)=>state.mousePos);
    const pinnedCoords = usePanelTooltipStore((state)=>state.pinnedCoords);
    const activePanelKey = usePanelTooltipStore((state)=>state.activePanelKey);
    const pinnedPanelKey = usePanelTooltipStore((state)=>state.pinnedPanelKey);
    return {
        mousePos,
        pinnedCoords,
        activePanelKey,
        pinnedPanelKey
    };
}
export { PanelTooltipProvider, usePanelTooltipActions, usePanelTooltipState, usePanelTooltipStore };

//# sourceMappingURL=PanelTooltipProvider.js.map